'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import {
  ShieldCheck,
  Activity,
  ScanLine,
  Bug,
  Server,
  Wrench,
  Boxes,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Search,
} from 'lucide-react';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

// Each option carries a risk weight: higher = more exposure. A `gap` string is
// surfaced in the results when the answer indicates a meaningful weakness.
interface Option {
  text: string;
  risk: number; // 0 (best) .. 3 (worst)
  gap?: string;
}

interface Question {
  id: string;
  text: string;
  helpText?: string;
  icon: React.ComponentType<{ className?: string }>;
  options: Option[];
}

interface Answer {
  questionId: string;
  optionIndex: number;
}

const QUESTIONS: Question[] = [
  {
    id: 'scan_cadence',
    text: 'How often do you run vulnerability scans across your environment?',
    helpText: 'New CVEs land daily. Point-in-time scans go stale within weeks.',
    icon: ScanLine,
    options: [
      { text: 'Continuously / weekly (automated)', risk: 0 },
      { text: 'Monthly', risk: 1 },
      { text: 'A few times a year', risk: 2, gap: 'Scanning is infrequent — newly disclosed CVEs sit undetected for weeks or months between scans.' },
      { text: 'Never, or only when something breaks', risk: 3, gap: 'No regular vulnerability scanning — you have no baseline of what is exposed or exploitable right now.' },
    ],
  },
  {
    id: 'last_pentest',
    text: 'When was your last penetration test (manual or automated)?',
    helpText: 'A scan finds known weaknesses; a pentest proves which ones are actually exploitable.',
    icon: Bug,
    options: [
      { text: 'Within the last 6 months, or continuous/automated', risk: 0 },
      { text: '6–12 months ago', risk: 1 },
      { text: 'More than a year ago', risk: 2, gap: 'Last pentest is over a year old — your environment has drifted, and untested attack paths may now be exploitable.' },
      { text: 'Never had one', risk: 3, gap: 'No penetration test on record — you have never validated which vulnerabilities a real attacker could chain into a breach.' },
    ],
  },
  {
    id: 'exposed_services',
    text: 'How well do you track what is exposed to the public internet?',
    helpText: 'Forgotten RDP, admin panels, dev servers, and old subdomains are where breaches start.',
    icon: Server,
    options: [
      { text: 'Continuously monitored external attack surface', risk: 0 },
      { text: 'We have a documented list we review periodically', risk: 1 },
      { text: 'Roughly — we know the big stuff but not everything', risk: 2, gap: 'Partial visibility into internet-facing assets — shadow services and forgotten subdomains are likely exposed without your knowledge.' },
      { text: 'We are not really sure what is exposed', risk: 3, gap: 'Unknown external attack surface — exposed RDP, admin panels, or dev systems could be reachable by attackers right now.' },
    ],
  },
  {
    id: 'patch_process',
    text: 'How do you handle patching of critical vulnerabilities?',
    helpText: 'Most breaches exploit a known vulnerability that had a patch available.',
    icon: Wrench,
    options: [
      { text: 'Risk-prioritized patching with SLAs (e.g. criticals in days)', risk: 0 },
      { text: 'Regular monthly patch cycle', risk: 1 },
      { text: 'Patch when we get to it / mostly auto-updates', risk: 2, gap: 'No prioritized patch SLA — critical, actively-exploited CVEs may sit unpatched well past the window attackers need.' },
      { text: 'No formal patching process', risk: 3, gap: 'No defined patch process — known, fixable vulnerabilities are accumulating and going unremediated.' },
    ],
  },
  {
    id: 'asset_inventory',
    text: 'How complete and current is your asset inventory?',
    helpText: 'You cannot scan, patch, or defend an asset you do not know you own.',
    icon: Boxes,
    options: [
      { text: 'Automated, continuously-updated inventory', risk: 0 },
      { text: 'A maintained spreadsheet/CMDB we keep reasonably current', risk: 1 },
      { text: 'A partial or outdated list', risk: 2, gap: 'Asset inventory is incomplete or stale — unknown endpoints and servers fall outside every scan and patch cycle.' },
      { text: 'No real inventory of devices and services', risk: 3, gap: 'No asset inventory — there is no way to confirm your scanning and patching actually cover everything you own.' },
    ],
  },
  {
    id: 'prioritization',
    text: 'When a scan returns hundreds of findings, how do you decide what to fix first?',
    helpText: 'Raw CVSS scores overstate risk. What matters is exploitability in your environment.',
    icon: Search,
    options: [
      { text: 'We prioritize by real-world exploitability and business impact', risk: 0 },
      { text: 'We work top-down by CVSS severity', risk: 1, gap: 'Prioritizing by CVSS alone — effort gets spread across findings that are not actually reachable or exploitable for you.' },
      { text: 'We fix what we can and hope we got the important ones', risk: 2, gap: 'No structured prioritization — the genuinely exploitable, attacker-reachable issues can get buried under noise.' },
      { text: 'We get the report but rarely act on most of it', risk: 3, gap: 'Findings are not being remediated — known exploitable vulnerabilities are being acknowledged but left open.' },
    ],
  },
];

const MAX_RISK = QUESTIONS.length * 3;

type Status = 'intro' | 'assessment' | 'results';

interface RiskResult {
  band: 'low' | 'moderate' | 'high' | 'critical';
  title: string;
  color: string;
  bgColor: string;
  scorePct: number;
  summary: string;
}

function getResult(answers: Answer[]): RiskResult {
  const total = answers.reduce((sum, a) => {
    const q = QUESTIONS.find((x) => x.id === a.questionId);
    return sum + (q ? q.options[a.optionIndex].risk : 0);
  }, 0);
  const scorePct = Math.round((total / MAX_RISK) * 100);

  if (scorePct >= 70) {
    return {
      band: 'critical',
      title: 'Critical Exposure',
      color: 'text-red-700 dark:text-red-300',
      bgColor: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
      scorePct,
      summary:
        'Your environment has significant blind spots that an attacker could exploit today. Establishing continuous scanning, a validated attack-surface baseline, and prioritized remediation should be treated as urgent.',
    };
  }
  if (scorePct >= 45) {
    return {
      band: 'high',
      title: 'High Exposure',
      color: 'text-orange-700 dark:text-orange-300',
      bgColor: 'bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-800',
      scorePct,
      summary:
        'You have meaningful gaps in how you find and fix vulnerabilities. The basics exist in places, but the program is not continuous or prioritized enough to stay ahead of newly disclosed and actively exploited CVEs.',
    };
  }
  if (scorePct >= 20) {
    return {
      band: 'moderate',
      title: 'Moderate Exposure',
      color: 'text-yellow-700 dark:text-yellow-300',
      bgColor: 'bg-yellow-50 dark:bg-yellow-950/40 border-yellow-200 dark:border-yellow-800',
      scorePct,
      summary:
        'You have a reasonable foundation, with a few gaps worth closing. Tightening cadence, prioritization, and attack-surface coverage will move you from "good enough" to genuinely resilient.',
    };
  }
  return {
    band: 'low',
    title: 'Low Exposure',
    color: 'text-green-700 dark:text-green-300',
    bgColor: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800',
    scorePct,
    summary:
      'Your vulnerability management practices are mature. The priority now is sustaining them — continuous validation that controls keep working as your environment changes.',
  };
}

export default function AssessmentClient() {
  const [status, setStatus] = useState<Status>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [endpointCount, setEndpointCount] = useState<number>(50);

  const handleAnswer = (optionIndex: number) => {
    const question = QUESTIONS[currentIndex];
    const newAnswer: Answer = { questionId: question.id, optionIndex };
    const newAnswers = [...answers.filter((a) => a.questionId !== question.id), newAnswer];
    setAnswers(newAnswers);

    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStatus('results');
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRestart = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setStatus('intro');
  };

  const result = useMemo(() => {
    if (answers.length === QUESTIONS.length) return getResult(answers);
    return null;
  }, [answers]);

  const gaps = useMemo(() => {
    return answers
      .map((a) => {
        const q = QUESTIONS.find((x) => x.id === a.questionId);
        return q?.options[a.optionIndex].gap;
      })
      .filter((g): g is string => Boolean(g));
  }, [answers]);

  const resultsSummary = useMemo(() => {
    if (!result) return '';
    const answerLines = answers
      .map((a) => {
        const q = QUESTIONS.find((x) => x.id === a.questionId);
        if (!q) return '';
        return `  • ${q.text}\n      ${q.options[a.optionIndex].text}`;
      })
      .filter(Boolean)
      .join('\n');
    const gapLines = gaps.length
      ? gaps.map((g, i) => `${i + 1}. ${g}`).join('\n')
      : 'No major gaps flagged — mature practices reported.';
    return `EXPOSURE RISK ASSESSMENT RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Exposure Risk: ${result.title} (${result.scorePct}/100)
Approx. endpoints / assets in scope: ${endpointCount}

${result.summary}

ANSWERS:
${answerLines}

PRIORITIZED GAPS TO CLOSE:
${gapLines}`;
  }, [result, answers, gaps, endpointCount]);

  // ---- Intro ----
  if (status === 'intro') {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Hero />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
            <div className="text-center mb-8">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] shadow-lg">
                <ShieldCheck className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                Exposure Risk Assessment
              </h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
                Six questions about your scanning, testing, exposed services, patching, and asset
                inventory. We compute an Exposure Risk score and hand you a prioritized list of the
                gaps an attacker would target first.
              </p>
            </div>

            <div className="bg-[#2813e8]/5 dark:bg-[#2813e8]/10 border border-[#2813e8]/20 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-[#2813e8] mb-2">What you&apos;ll get:</h3>
              <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-1">
                <li>• An Exposure Risk score from Low to Critical</li>
                <li>• A prioritized list of your specific gaps</li>
                <li>• Whether you need continuous scanning, a pentest, or both</li>
                <li>• A clear next step for your environment</li>
              </ul>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-6">
              <span>Takes about 2 minutes</span>
              <span>No tools to install</span>
            </div>

            <button
              onClick={() => setStatus('assessment')}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white font-semibold py-3 px-6 rounded-lg transition-all hover:scale-[1.02] hover:shadow-xl"
            >
              Start the Assessment
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Assessment ----
  if (status === 'assessment') {
    const question = QUESTIONS[currentIndex];
    const Icon = question.icon;
    const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
    const currentAnswer = answers.find((a) => a.questionId === question.id);

    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Hero />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span className="inline-flex items-center gap-2">
                  <Icon className="h-4 w-4 text-[#2813e8]" />
                  Exposure factor
                </span>
                <span>
                  Question {currentIndex + 1} of {QUESTIONS.length}
                </span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {question.text}
              </h2>
              {question.helpText && (
                <p className="text-gray-600 dark:text-gray-400 text-sm">{question.helpText}</p>
              )}
            </div>

            <div className="space-y-3 mb-6">
              {question.options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    currentAnswer?.optionIndex === idx
                      ? 'border-[#2813e8] bg-[#2813e8]/5 dark:bg-[#2813e8]/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-[#2cc3d1] hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="text-gray-900 dark:text-gray-100">{option.text}</span>
                </button>
              ))}
            </div>

            <div className="flex justify-between">
              <button
                onClick={handleBack}
                disabled={currentIndex === 0}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Back
              </button>
              <button
                onClick={handleRestart}
                className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Results ----
  if (status === 'results' && result) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Hero />
        <div className="max-w-3xl mx-auto px-4 py-16 space-y-6">
          {/* Score */}
          <div className={`rounded-2xl border-2 p-8 ${result.bgColor}`}>
            <div className="text-center mb-6">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/70 dark:bg-gray-900/50 shadow">
                {result.band === 'low' ? (
                  <ShieldCheck className={`h-8 w-8 ${result.color}`} />
                ) : (
                  <AlertTriangle className={`h-8 w-8 ${result.color}`} />
                )}
              </div>
              <div className="text-sm uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1">
                Your Exposure Risk
              </div>
              <h2 className={`text-3xl font-bold mb-1 ${result.color}`}>{result.title}</h2>
              <div className={`text-5xl font-extrabold ${result.color}`}>
                {result.scorePct}
                <span className="text-2xl font-semibold opacity-70">/100</span>
              </div>
            </div>
            <p className="text-gray-700 dark:text-gray-300 text-center max-w-xl mx-auto leading-relaxed">
              {result.summary}
            </p>
          </div>

          {/* Endpoint count */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Approximate endpoints / assets in scope:{' '}
              <span className="text-[#2813e8] dark:text-[#2cc3d1] font-bold">{endpointCount}</span>
            </label>
            <input
              type="range"
              min="5"
              max="500"
              step="5"
              value={endpointCount}
              onChange={(e) => setEndpointCount(Number(e.target.value))}
              aria-label="Number of endpoints or assets"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>5</span>
              <span>250</span>
              <span>500+</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              We use this to scope the right scanning and testing cadence for your environment.
            </p>
          </div>

          {/* Gaps */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Bug className="h-5 w-5 text-[#2813e8]" />
              {gaps.length ? 'Prioritized Gaps to Close' : 'No Major Gaps Flagged'}
            </h3>
            {gaps.length ? (
              <ul className="space-y-3">
                {gaps.map((gap, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700"
                  >
                    <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{gap}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                Your answers indicate mature practices. The priority now is continuous validation
                that controls keep working as your environment changes.
              </p>
            )}
          </div>

          {/* Scanning vs pentest guidance + CTAs to BOTH existing offer pages */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
              What this points you toward
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border-2 border-gray-200 dark:border-gray-700 p-5 flex flex-col">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#2813e8]/10">
                  <ScanLine className="h-5 w-5 text-[#2813e8]" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Continuous Vulnerability Management
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 flex-grow">
                  Always-on external + internal scanning, an accurate asset inventory, and
                  exploitability-based prioritization so you fix what actually matters.
                </p>
                <Link
                  href="/services/vulnerability-management"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#2813e8] dark:text-[#2cc3d1] hover:gap-3 transition-all"
                >
                  Explore Vulnerability Management
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="rounded-xl border-2 border-gray-200 dark:border-gray-700 p-5 flex flex-col">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#2cc3d1]/10">
                  <Bug className="h-5 w-5 text-[#2cc3d1]" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Penetration Testing
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 flex-grow">
                  Validate which vulnerabilities are truly exploitable — manual testing plus
                  autonomous pentest — and prove your defenses hold under real attack.
                </p>
                <Link
                  href="/services/penetration-testing"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#2813e8] dark:text-[#2cc3d1] hover:gap-3 transition-all"
                >
                  Explore Penetration Testing
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              Not sure which you need? Read{' '}
              <Link
                href="/services/vulnerability-management/vulnerability-scanning-vs-penetration-testing"
                className="text-[#2813e8] dark:text-[#2cc3d1] underline hover:no-underline"
              >
                vulnerability scanning vs. penetration testing
              </Link>{' '}
              — most organizations need both.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleRestart}
              className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-6 rounded-lg transition-colors"
            >
              Retake Assessment
            </button>
          </div>

          {/* Lead capture — tagged src:'vuln' */}
          <div className="mt-4">
            <LeadCaptureForm
              source="vuln"
              title="Get an Expert Review of Your Exposure"
              description="A CISSP-led review of your results, with a scoped plan for continuous scanning and validation tailored to your environment."
              deliverable="a prioritized exposure-reduction plan with scanning and testing recommendations"
              resultsSummary={resultsSummary}
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function Hero() {
  return (
    <section className="relative bg-gradient-to-br from-[#2813e8] via-[#2813e8] to-[#2cc3d1] text-white py-16 px-4 overflow-hidden">
      <div className="absolute inset-0 bg-black/10" />
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-16 w-72 h-72 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-16 w-96 h-96 bg-[#2cc3d1] rounded-full blur-3xl" />
      </div>
      <div className="max-w-3xl mx-auto relative z-10 text-center">
        <nav className="mb-6 text-sm">
          <ol className="flex items-center justify-center space-x-2 text-blue-100">
            <li>
              <Link href="/" className="hover:text-white transition-colors">
                Home
              </Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li className="text-white font-semibold">Exposure Risk Assessment</li>
          </ol>
        </nav>
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2 text-sm font-medium backdrop-blur-md border border-white/20">
          <Activity className="h-4 w-4" />
          <span>Free · CISSP-built · No tools to install</span>
        </div>
        <h1 className="text-3xl md:text-5xl font-bold leading-tight">
          You can&apos;t fix what you can&apos;t see
        </h1>
        <p className="mt-4 text-lg md:text-xl opacity-95 max-w-2xl mx-auto">
          Find the exploitable holes in your environment before attackers do. Get your Exposure Risk
          score and a prioritized gap list in under three minutes.
        </p>
      </div>
    </section>
  );
}
